// `extension-alert-exchange-login-modal.tpl.html` and its service — "Login to your exchange", the
// dialog the Microsoft Exchange button raises on both "Please connect your work email" screens.
//
// Opened upstream through `modalService.show(…, { useUnifiedTemplate: true, windowClass:
// "dialog-unified shortlist-dialogue shortlist-exchange-login-modal" })`, so the outer chain is the
// unified template's, and `_extension-alert-exchange-login-modal.scss` selects on it: the 400px
// width hangs off `.shortlist-exchange-login-modal .modal-dialog`.
//
// ⚠️ A WRONG PASSWORD IS `success: false`, AND ONLY THAT GETS A MESSAGE. Upstream reports it with
// `$rootScope.showAlert(…)` over the dialog, which stays open so the user can correct it
// (`closeAfterOk: false`: only success closes it). A request that FAILS — measured on the dev
// tenant, made-up credentials answer HTTP 500 — is an unhandled rejection upstream: nothing is
// shown and the dialog stays as it was. Kept, because a message there would be a redesign.
import { useState, type ReactElement } from 'react'
import { MessageDialog } from '../common/MessageDialog.tsx'
import { UnifiedModal } from '../modal/UnifiedModal.tsx'

/** Upstream's alert text, verbatim — including "exchange" in lower case. */
export const EXCHANGE_LOGIN_FAILED_MESSAGE =
  'Error connecting to exchange, please make sure you use the right username & password'

export interface ExchangeLoginModalProps {
  /** `validateAndSave` — resolves `true` when the tenant accepted the credentials. */
  onSignIn: (username: string, password: string) => Promise<boolean>
  /** Called after a successful sign-in, once the dialog has closed. */
  onConnected?: () => void
  /** The ✕. */
  onCancel: () => void
}

export function ExchangeLoginModal({ onSignIn, onConnected, onCancel }: ExchangeLoginModalProps): ReactElement {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [signingIn, setSigningIn] = useState(false)
  const [failed, setFailed] = useState(false)
  const usernameMissing = username === ''
  const passwordMissing = password === ''

  /** `signin()`: `$setSubmitted`, stop on an invalid form, else validate and save. */
  const signIn = (): void => {
    if (signingIn) return
    setSubmitted(true)
    if (usernameMissing || passwordMissing) return
    setSigningIn(true)
    onSignIn(username, password)
      .then(
        (success) => {
          if (!success) {
            setFailed(true)
            return
          }
          onConnected?.()
        },
        // Unhandled upstream; see the header.
        () => undefined,
      )
      .finally(() => {
        setSigningIn(false)
      })
  }

  return (
    <>
      <UnifiedModal
        open
        windowClass="dialog-unified shortlist-dialogue shortlist-exchange-login-modal"
        onClose={onCancel}
      >
        <div className="shortlist-exchange-login-modal-container">
          <div className="shortlist-dialogue-x-icon">
            <i
              className="far fa-times"
              role="button"
              aria-label="Close"
              tabIndex={0}
              onClick={onCancel}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return
                event.preventDefault()
                onCancel()
              }}
            />
          </div>
          <div className="exchange-login-header-modal">
            <span>Login to your exchange</span>
          </div>
          <div className="nicer-scroller four-px-scroll shortlist-exchange-login-modal-body">
            <form
              className="shortlist-exchange-login-modal-form"
              name="form"
              noValidate
              onSubmit={(event) => {
                event.preventDefault()
                signIn()
              }}
            >
              <div className="mT20">
                <input
                  className={`form-control${usernameMissing && submitted ? ' form-error' : ''}`}
                  type="text"
                  placeholder="Username"
                  name="username"
                  value={username}
                  tabIndex={1}
                  autoFocus
                  required
                  onChange={(event) => {
                    setUsername(event.target.value)
                  }}
                />
                {/* `ng-show`; rendered conditionally, which draws the same (see DeleteAlertPage). */}
                {usernameMissing && submitted ? (
                  <label className="control-label has-error error-label">Please enter a username</label>
                ) : null}
              </div>
              <div className="mT20">
                <input
                  className={`form-control${passwordMissing && submitted ? ' form-error' : ''}`}
                  type="password"
                  placeholder="Password"
                  name="password"
                  value={password}
                  tabIndex={2}
                  required
                  onChange={(event) => {
                    setPassword(event.target.value)
                  }}
                />
                {passwordMissing && submitted ? (
                  <label className="control-label has-error error-label">Please enter a password</label>
                ) : null}
              </div>
              {/* An `ng-form` has no submit button, but Enter in a field should still sign in. */}
              <input type="submit" hidden />
            </form>
          </div>
          <div className="exchange-login-modal-footer">
            <div className="exchange-login-footer-ctas">
              {/* `click-and-disable`: inert while the request is in flight. */}
              <input
                type="button"
                className="exchange-login-footer-login-button"
                value="Login"
                disabled={signingIn}
                onClick={signIn}
              />
            </div>
          </div>
        </div>
      </UnifiedModal>
      {!failed ? null : (
        <MessageDialog
          text={EXCHANGE_LOGIN_FAILED_MESSAGE}
          okLabel="Ok"
          size="md"
          onOk={() => {
            setFailed(false)
          }}
        />
      )}
    </>
  )
}
